import { HttpClient } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { catchError, Observable, retry, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FeatureFlagClientDTO } from '../dto';
import { AbstractService } from './abstractservice';

/**
 * Loaded once immediately after login (see AuthService) and cached for the life of the
 * session - there is no polling and no live update. A flag flipped by a developer mid-session
 * is only picked up the next time this player logs in.
 */
@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {

    // Written from loadFlags(), called by AuthService at login/cookie-login/refresh - never
    // from a template event this service's own consumers own, so the storage has to be a
    // signal for OnPush template reads (e.g. AppComponent's lightswitch footer) to stay live.
    // isFlagOpen/isEnabled take an argument, so they stay plain methods reading through the
    // signal rather than becoming computed()s themselves.
    private flagsByIdState: WritableSignal<Map<number, boolean>> = signal(new Map<number, boolean>());
    private flagsByCodeNameState: WritableSignal<Map<string, boolean>> = signal(new Map<string, boolean>());

    constructor(private http: HttpClient) { }

    public loadFlags(jwtToken: string): Observable<FeatureFlagClientDTO[]> {
        return this.http.get<FeatureFlagClientDTO[]>(environment.apiUrl + '/feature-flags/client', AbstractService.httpOptionsAuthJson(jwtToken))
            .pipe(
                retry(2),
                tap((flags: FeatureFlagClientDTO[]) => {
                    this.flagsByIdState.set(new Map(flags.map(f => [f.Id, f.Enabled])));
                    this.flagsByCodeNameState.set(new Map(flags.map(f => [f.CodeName, f.Enabled])));
                }),
                catchError(AbstractService.handleError)
            );
    }

    public isEnabled(id: number): boolean {
        return this.flagsByIdState().get(id) ?? false;
    }

    /**
     * Looks a flag up by its opaque CodeName. Lets UI code ship against a flag before it's ever
     * loaded (not yet created in the DB, or not present in this session's snapshot):
     * failOpen controls what happens then - true allows the feature, false blocks it.
     */
    public isFlagOpen(codeName: string, failOpen: boolean): boolean {
        const enabled = this.flagsByCodeNameState().get(codeName);
        return enabled ?? failOpen;
    }
}
